import React, { FC, Fragment, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { Button, createStyles, makeStyles, Typography } from '@material-ui/core';
import { Formik, FormikHelpers, FormikProps, FormikValues } from 'formik';
import { TFunction } from 'i18next';
import * as Yup from 'yup';
import { ModalHeader, ModalContainer, ModalFooter, ModalContent } from 'components/@common/Modal';
import PageContainer from 'components/@layout/PageContainer';
import TRANSFER_MASTERCODE from 'graph/orders/transfer-mastercode-mutation';
import TRANSFER_ORDER_MASTERCODES from 'graph/orders/transfer-order-mastercode-mutation';
import useUiStore from 'store/ui';
import useUserStore from 'store/user';
import { AlertOptions, Theme, VoidCallback } from 'types';
import { AlertVariant } from 'types/enums';
import { Mastercode, Order } from 'types/schema-generated-types';
import getOrderNumber from 'utilities/get-order-number';
import { getMyOrdersMutationCacheUpdate } from '../../utils/cacheUpdater';
import MastercodeModalDetails from '../components/MastercodeModalDetails';
import TransferMastercodeForm from './components/TransferMastercodeForm';

interface TransferMastercodeModalProps {
  transferAll?: boolean;
  order: Order;
  mastercodeId?: number;
  ownerEmail?: string | Array<string> | null;
  onClose: VoidCallback;
  onSubmit?: VoidCallback;
}

interface TransferMastercodeForm {
  firstName: string;
  lastName: string;
  email: string;
  confirmationEmail: string;
}

const getSuccessAlert = (t: TFunction, html: ReactElement): AlertOptions => ({
  variant: AlertVariant.SUCCESS,
  title: t('You have successfully transferred your code(s).'),
  html,
  confirmButtonText: t('Close')
});

const getWarningAlert = (t: TFunction, html: ReactElement): AlertOptions => ({
  variant: AlertVariant.WARNING,
  title: t('confirmMastercodeTransfer'),
  html,
  confirmButtonText: t('Transfer')
});

const TransferMastercodeModal: FC<TransferMastercodeModalProps> = ({
  transferAll = false,
  order,
  ownerEmail,
  mastercodeId,
  onClose,
  onSubmit: onSubmitHandlerProp
}) => {
  const classes = useStyles();
  const { t } = useTranslation('Order');
  const [, { closeModal, openAlert }] = useUiStore();
  const [{ me }] = useUserStore();

  const [transferMastercode] = useMutation(
    TRANSFER_MASTERCODE,
    getMyOrdersMutationCacheUpdate(me?.id)
  );

  const [transferOrderMastercodes] = useMutation(
    TRANSFER_ORDER_MASTERCODES,
    getMyOrdersMutationCacheUpdate(me?.id)
  );

  const validateForm = (values: FormikValues): FormikValues => {
    const errors: FormikValues = {};
    if (values.email !== values.confirmationEmail) {
      errors.confirmationEmail = 'Emails are different';
    }

    return errors;
  };

  const orderWarningAlertHTML: ReactElement = (
    <div>
      <p>{t('orderNumber', { orderNumber: getOrderNumber(order) })}</p>
    </div>
  );

  const orderSuccessAlertHTML: ReactElement = <Fragment>{orderWarningAlertHTML}</Fragment>;

  const mastercodesToTransfer: Mastercode[] = [];
  for (const mastercode of order.mastercodes ?? []) {
    if (mastercode.dateUnlocked) continue;
    if (mastercodeId && mastercodeId !== mastercode.id) continue;

    mastercodesToTransfer.push(mastercode);
  }

  const handleOnConfirmMastercodeModal = async ({
    firstName,
    lastName,
    email
  }: FormikValues): Promise<void> => {
    try {
      await transferMastercode({
        variables: {
          ownerEmail,
          ...(mastercodeId && { mastercodeId }),
          data: { firstName, lastName, ownerEmail: email }
        }
      });
      openAlert(getSuccessAlert(t, orderSuccessAlertHTML));
    } catch (error) {
      //TODO: Handle Error
      console.log('Error', error);
    }
  };

  const handleOnConfirmOrderModal = async ({
    firstName,
    lastName,
    email
  }: FormikValues): Promise<void> => {
    try {
      await transferOrderMastercodes({
        variables: {
          orderId: order.id,
          ownerEmail,
          data: { firstName, lastName, ownerEmail: email }
        }
      });
      openAlert(getSuccessAlert(t, orderSuccessAlertHTML));
    } catch (error) {
      //TODO: Handle Error
      console.log('Error', error);
    }
  };

  const handleFormikSubmit = (
    values: FormikValues,
    { setSubmitting }: FormikHelpers<TransferMastercodeForm>
  ): void => {
    openAlert({
      ...getWarningAlert(t, orderWarningAlertHTML),
      onClose: () => {
        setSubmitting(false);
      },
      onSubmit: async result => {
        if (!result.value) return;
        if (transferAll) await handleOnConfirmOrderModal(values);
        else await handleOnConfirmMastercodeModal(values);
        if (onSubmitHandlerProp) await onSubmitHandlerProp();

        closeModal();
      }
    });
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{
        firstName: '',
        lastName: '',
        email: '',
        confirmationEmail: ''
      }}
      validationSchema={Yup.object({
        firstName: Yup.string(),
        lastName: Yup.string(),
        email: Yup.string().email('Invalid email'),
        confirmationEmail: Yup.string().email('Invalid email')
      })}
      isInitialValid={false}
      validateOnChange={true}
      validate={validateForm}
      onSubmit={handleFormikSubmit}
    >
      {({
        isSubmitting,
        isValid,
        submitForm
      }: FormikProps<TransferMastercodeForm>): ReactElement => (
        <PageContainer showChildrenWhileLoading>
          <ModalContainer
            Header={
              <ModalHeader>
                <Typography noWrap variant="h2" component="h1">
                  {t('orderNumber', { orderNumber: getOrderNumber(order) })}
                </Typography>
              </ModalHeader>
            }
            Footer={
              <ModalFooter
                Actions={
                  <>
                    <Button
                      aria-label={t('Cancel')}
                      color="secondary"
                      variant="outlined"
                      disableElevation
                      onClick={onClose}
                    >
                      {t('Cancel')}
                    </Button>
                    <Button
                      aria-label={t('Confirm & Transfer')}
                      variant="contained"
                      color="primary"
                      disableElevation
                      onClick={submitForm}
                      disabled={isSubmitting || !isValid}
                    >
                      {t('Confirm & Transfer')}
                    </Button>
                  </>
                }
              />
            }
          >
            <ModalContent className={classes.body}>
              <MastercodeModalDetails order={order} mastercodes={mastercodesToTransfer} />
              <TransferMastercodeForm />
            </ModalContent>
          </ModalContainer>
        </PageContainer>
      )}
    </Formik>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    body: {
      padding: theme.spacing(1)
    }
  })
);

export default TransferMastercodeModal;
